package pl.zar.tikthrow.shared.paging

import kotlinx.serialization.Serializable
import pl.zar.tikthrow.shared.dto.ApiException

/**
 * Cursor pagination contract (card #25 / T109; spec FR-016).
 *
 * Every listing on `/api/v1` accepts `?limit=` (integer 1..250, default
 * [Pagination.DEFAULT_LIMIT]) and `?cursor=` (the opaque
 * [PagedResult.nextCursor] of the previous page) and answers a
 * [PagedResult]. Ordering is stable: `(created_at DESC, id DESC)`, so paging
 * a full listing never duplicates or skips a row. (The MCP tools of card
 * #19 — not in this repository yet — will list with the same two parameters,
 * fed as tool arguments through this same parse.)
 */

/**
 * One page of a cursor listing.
 *
 * @property items the rows of this page (at most `limit`).
 * @property nextCursor pass it back as `?cursor=` to fetch the next page;
 *   null = this is the last page.
 */
@Serializable
data class PagedResult<T>(
    val items: List<T>,
    val nextCursor: String?,
)

/** One stable sort key: the `(created_at, id)` pair that orders a listing. */
data class KeysetCursor(val epochNanos: Long, val id: String)

/** Parsed and validated pagination parameters for one request. */
data class PaginationParams(val limit: Int, val cursor: KeysetCursor?)

object Pagination {
    const val DEFAULT_LIMIT = 50
    const val MAX_LIMIT = 250

    /**
     * Parses and validates the `limit` and `cursor` query parameters
     * (REST query string; the planned MCP tools of card #19 must parse the
     * same two values with this same function).
     *
     * @param limitRaw raw `limit` value, or null when absent (default 50).
     * @param cursorRaw raw `cursor` value, or null when absent (first page).
     * @throws ApiException a 400 [pl.zar.tikthrow.shared.dto.ApiErrorCode.VALIDATION_ERROR]
     *   with `details.field` naming the offending parameter, when either
     *   value is invalid (`limit` not an integer in 1..250, `cursor` not a
     *   well-formed opaque cursor).
     */
    fun parseParams(limitRaw: String?, cursorRaw: String?): PaginationParams {
        val limit = limitRaw?.let { raw ->
            raw.toIntOrNull()
                ?.takeIf { it in 1..MAX_LIMIT }
                ?: throw ApiException(
                    field = "limit",
                    message = "limit must be an integer between 1 and $MAX_LIMIT",
                )
        } ?: DEFAULT_LIMIT

        val cursor = cursorRaw?.takeIf { it.isNotEmpty() }?.let { raw ->
            CursorCodec.decode(raw)
                ?: throw ApiException(
                    field = "cursor",
                    message = "cursor is not a valid pagination cursor",
                )
        }
        return PaginationParams(limit, cursor)
    }
}

/**
 * Opaque, URL-safe cursor encoding: `tikth1.<epochNanos>.<id>`.
 *
 * No base64: all characters are already URL-safe, and the format is trivially
 * debuggable. `id` is the row primary key as a UUID string (the keyset
 * helper only paginates UUID-keyed tables); [decode] rejects anything that
 * does not match the shape exactly, so a hostile `?cursor=` cannot inject
 * SQL or shift the page boundary.
 */
object CursorCodec {
    private const val PREFIX = "tikth1"

    /** Encodes one stable sort key into the opaque cursor string. */
    fun encode(epochNanos: Long, id: String): String = "$PREFIX.$epochNanos.$id"

    /** Decodes an opaque cursor; null when the string is not well-formed. */
    fun decode(cursor: String): KeysetCursor? = runCatching {
        val parts = cursor.split('.', limit = 3)
        require(parts.size == 3)
        require(parts[0] == PREFIX)
        require(parts[1].all { it.isDigit() } && parts[1].length <= 19)
        require(parts[2].isUuid())
        KeysetCursor(epochNanos = parts[1].toLong(), id = parts[2])
    }.getOrNull()

    private fun String.isUuid(): Boolean {
        if (length != 36) return false
        if (this[8] != '-' || this[13] != '-' || this[18] != '-' || this[23] != '-') return false
        if (count { it == '-' } != 4) return false
        return all { isHex(it) }
    }

    private fun isHex(c: Char): Boolean =
        c == '-' || c.isDigit() || c in 'a'..'f' || c in 'A'..'F'
}
